import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import { getAuth, type Auth } from "firebase/auth";
import { getFirestore, type Firestore } from "firebase/firestore";
import { getFunctions, type Functions } from "firebase/functions";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from "firebase/app-check";

export interface FirebaseClientConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  /**
   * reCAPTCHA v3 (or Enterprise) site key for Firebase App Check. OPTIONAL:
   * when absent, App Check is not initialized and the client behaves exactly
   * as before — so this can ship before the key is provisioned in the console.
   * Once set, the Firebase SDK auto-attaches an App Check token to every
   * callable invocation (and Firestore/Storage), satisfying server-side
   * App Check verification. See artifacts/functions/src/lib/appCheck.ts.
   */
  appCheckSiteKey?: string;
  /**
   * Local-dev App Check debug token. When set, registers it on
   * `self.FIREBASE_APPCHECK_DEBUG_TOKEN` before init so a developer machine /
   * the preview iframe can obtain tokens without a real reCAPTCHA challenge.
   */
  appCheckDebugToken?: string;
}

export interface FirebaseClient {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  functions: Functions;
  appCheck: AppCheck | null;
}

let client: FirebaseClient | null = null;

export function initializeFirebaseClient(
  config: FirebaseClientConfig,
  region = "europe-west1",
): FirebaseClient {
  if (client) return client;
  const app = getApps().length > 0 ? getApps()[0]! : initializeApp(config);

  // App Check — initialize BEFORE auth/firestore/functions so the SDK attaches
  // tokens to every subsequent request. Gated on the site key: with no key we
  // skip it entirely and the client is unchanged (safe to ship pre-provision).
  let appCheck: AppCheck | null = null;
  if (config.appCheckSiteKey) {
    if (
      config.appCheckDebugToken &&
      typeof self !== "undefined"
    ) {
      (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
        config.appCheckDebugToken;
    }
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(config.appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  }

  const auth = getAuth(app);
  const db = getFirestore(app);
  const functions = getFunctions(app, region);
  client = { app, auth, db, functions, appCheck };
  return client;
}

export function getFirebaseClient(): FirebaseClient {
  if (!client) {
    throw new Error(
      "Firebase client not initialized. Call initializeFirebaseClient() first.",
    );
  }
  return client;
}
